import { useMemo } from "react";
import type { TAppStore } from "@/store";
import type { TApiClient } from "@/apis/proda_finance";

export const useDealDetailPropcodeVersionActions = (store: TAppStore, api: TApiClient) =>
    useMemo(
        () => ({
            fetchVersions: async (dealId: string) => {
                const state = store.getState();
                state.setVersionsLoading();
                try {
                    const data = await api.deals.getPropcodeVersions(dealId);
                    state.setVersionsList({
                        items: data.items.map((v) => ({
                            id: v.id,
                            dealId: v.deal_id,
                            versionNumber: v.version_number,
                            sourceType: v.source_type as "upload" | "snapshot",
                            storagePath: v.storage_path,
                            originalFilename: v.original_filename,
                            createdById: v.created_by_id,
                            createdByName: v.created_by_name,
                            mappingsCount: v.mappings_count,
                            description: v.description,
                            createdAt: v.created_at,
                        })),
                        total: data.total,
                        page: data.page,
                        pageSize: data.page_size,
                        hasMore: data.has_more ?? false,
                    });
                    state.setVersionSelectedDealId(dealId);
                } catch (err) {
                    state.setVersionsError(
                        err instanceof Error ? err.message : "Failed to fetch versions",
                    );
                    throw err;
                }
            },
            uploadVersion: async (dealId: string, file: File, description?: string) => {
                const state = store.getState();
                state.setVersionUploadLoading();
                try {
                    const result = await api.deals.uploadPropcodeVersion(dealId, file, description);
                    if (result.success) {
                        state.setVersionUploadSuccess({
                            success: result.success,
                            errors: result.errors,
                            warnings: result.warnings,
                            versionId: result.version_id,
                            versionNumber: result.version_number,
                            mappingsCount: result.mappings_count,
                            storagePath: result.storage_path,
                        });
                    } else {
                        state.setVersionUploadError(
                            result.errors?.length ? result.errors.join(", ") : "Upload failed",
                        );
                    }
                    return result;
                } catch (err) {
                    state.setVersionUploadError(
                        err instanceof Error ? err.message : "Upload failed",
                    );
                    throw err;
                }
            },
            createSnapshot: async (dealId: string, description?: string) => {
                const state = store.getState();
                state.setSnapshotLoading();
                try {
                    const result = await api.deals.createPropcodeSnapshot(dealId, description);
                    if (result.success) {
                        state.setSnapshotSuccess({
                            success: result.success,
                            errors: result.errors,
                            warnings: result.warnings,
                            versionId: result.version_id,
                            versionNumber: result.version_number,
                            mappingsCount: result.mappings_count,
                            storagePath: result.storage_path,
                        });
                    } else {
                        state.setSnapshotError(
                            result.errors?.length ? result.errors.join(", ") : "Snapshot failed",
                        );
                    }
                    return result;
                } catch (err) {
                    state.setSnapshotError(err instanceof Error ? err.message : "Snapshot failed");
                    throw err;
                }
            },
            restoreVersion: async (dealId: string, versionId: string) => {
                const state = store.getState();
                state.setRestoreLoading();
                try {
                    const result = await api.deals.restorePropcodeVersion(dealId, versionId);
                    if (result.success) {
                        state.setRestoreSuccess({
                            success: result.success,
                            errors: result.errors,
                            warnings: result.warnings,
                            mappingsDeleted: result.mappings_deleted,
                            mappingsCreated: result.mappings_created,
                        });
                    } else {
                        state.setRestoreError(
                            result.errors?.length ? result.errors.join(", ") : "Restore failed",
                        );
                    }
                    return result;
                } catch (err) {
                    state.setRestoreError(err instanceof Error ? err.message : "Restore failed");
                    throw err;
                }
            },
        }),
        [store],
    );
